// src/components/ProjectWorkspace.jsx
// ─────────────────────────────────────────────────────────
// The landing screen users see before opening a project.
// Shows all their saved projects and a "Create New" button.
// ─────────────────────────────────────────────────────────

import React, { useState } from 'react';
import { Building2, PlusCircle, Folder, ChevronRight } from 'lucide-react';

export default function ProjectWorkspace({ projects, onCreate, onSelect, onDelete }) {
  const [isCreating, setIsCreating] = useState(false);
  const [name, setName]             = useState('');

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    await onCreate(name.trim());
    setName('');
    setIsCreating(false);
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-6 font-sans">
      <div className="bg-indigo-50 p-4 rounded-2xl mb-6 shadow-inner">
        <Building2 className="w-10 h-10 text-indigo-600" />
      </div>
      <h1 className="text-3xl font-bold text-slate-900 mb-2">Project Workspace</h1>
      <p className="text-slate-500 mb-10 text-center max-w-md">
        Select an existing development project or create a new financial workspace.
      </p>

      <div className="w-full max-w-3xl grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {projects.map(p => (
          <div
            key={p.id}
            onClick={() => onSelect(p)}
            className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm hover:border-indigo-300 hover:shadow-md transition-all cursor-pointer group flex flex-col justify-between h-40"
          >
            <div>
              <Folder className="w-6 h-6 text-indigo-400 mb-3 group-hover:text-indigo-600 transition-colors" />
              <h3 className="font-bold text-slate-800 text-lg truncate">{p.name}</h3>
            </div>
            <div className="flex justify-between items-center text-sm font-medium text-indigo-600 gap-2">
              <span>Open Workspace</span>
              <div className="flex items-center gap-2">
                {!!onDelete && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(p.id);
                    }}
                    className="text-[11px] px-2 py-1 rounded-md font-bold bg-rose-50 text-rose-700 hover:bg-rose-100 border border-rose-100"
                  >
                    Delete
                  </button>
                )}
                <ChevronRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-all" />
              </div>
            </div>
          </div>
        ))}

        {/* Create New Card */}
        {isCreating ? (
          <form
            onSubmit={handleCreate}
            className="bg-white p-6 rounded-2xl border-2 border-dashed border-indigo-200 flex flex-col justify-center h-40 shadow-sm"
          >
            <input
              autoFocus
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Project Name..."
              className="w-full p-2.5 text-sm border border-slate-200 rounded-lg mb-3 focus:ring-2 focus:ring-indigo-500 outline-none"
            />
            <div className="flex gap-2">
              <button type="submit" className="flex-1 bg-indigo-600 text-white text-xs font-bold py-2 rounded-lg hover:bg-indigo-700">
                Create
              </button>
              <button
                type="button"
                onClick={() => { setIsCreating(false); setName(''); }}
                className="flex-1 bg-slate-100 text-slate-600 text-xs font-bold py-2 rounded-lg hover:bg-slate-200"
              >
                Cancel
              </button>
            </div>
          </form>
        ) : (
          <div
            onClick={() => setIsCreating(true)}
            className="bg-slate-50/50 p-6 rounded-2xl border-2 border-dashed border-slate-300 hover:border-indigo-400 hover:bg-indigo-50/50 transition-all cursor-pointer flex flex-col items-center justify-center text-slate-500 hover:text-indigo-600 h-40"
          >
            <PlusCircle className="w-8 h-8 mb-2 opacity-50" />
            <span className="font-medium text-sm">Create New Project</span>
          </div>
        )}
      </div>
    </div>
  );
}
